import { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { AdminI18nProvider } from '@/contexts/AdminI18nContext';
import Layout from '@/components/common/Layout';
import OnboardingPage from '@/pages/OnboardingPage';
import PairsPage from '@/pages/PairsPage';
import CalculatorPage from '@/pages/CalculatorPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPage from '@/pages/AdminPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import {
  addAdminAuthChangeListener,
  clearAdminToken,
  getAdminSession,
  getStoredAdminToken,
  isUnauthorizedError,
  storeAdminToken,
} from '@/services/api';

function useAdminAuth() {
  const [adminToken, setAdminToken] = useState(() => getStoredAdminToken());

  useEffect(() => {
    return addAdminAuthChangeListener(() => {
      setAdminToken(getStoredAdminToken());
    });
  }, []);

  const login = useCallback((token: string) => {
    storeAdminToken(token);
    setAdminToken(token);
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    setAdminToken(null);
  }, []);

  return { adminToken, isAdminLoggedIn: Boolean(adminToken), login, logout };
}

// Separate Admin App - completely independent authentication
function AdminApp() {
  const { adminToken, isAdminLoggedIn, login, logout } = useAdminAuth();
  const {
    error: adminSessionError,
    isLoading: isAdminSessionLoading,
  } = useQuery({
    queryKey: ['admin-session', adminToken],
    queryFn: getAdminSession,
    enabled: isAdminLoggedIn,
    retry: false,
  });

  useEffect(() => {
    if (isUnauthorizedError(adminSessionError)) {
      logout();
    }
  }, [adminSessionError, logout]);

  return (
    <AdminI18nProvider>
      {!isAdminLoggedIn || adminSessionError ? (
        <AdminLoginPage onLogin={login} />
      ) : isAdminSessionLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <AdminPage />
      )}
    </AdminI18nProvider>
  );
}

// Main User App - regular Telegram-based authentication
function UserApp() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show onboarding for new users
  if (user && !user.onboarding_completed) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  return (
    <Layout showAdminTab={false}>
      <Routes>
        <Route path="/" element={<PairsPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  const location = useLocation();

  if (location.pathname.startsWith('/mockboarding')) {
    return (
      <Routes>
        <Route path="/mockboarding" element={<OnboardingPage mockMode />} />
      </Routes>
    );
  }

  // Admin routes are completely separate
  if (location.pathname.startsWith('/admin')) {
    return (
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    );
  }

  // Regular user routes
  return <UserApp />;
}

export default App;
